// A `.tmj` somebody picked off their disk, read into a level source.
//
// The parser refuses a malformed *map* and names every fault; what it cannot
// see is a file that is not a map at all — a tileset picked by mistake, a
// half-saved file, a screenshot. Those are caught here, naming the file, so
// the sentence the panel shows says which of the two things went wrong.
//
// Pure: the panel reads the file's text and hands it here.

import type { LevelSource } from "src/engine/campaign/levels";
import type { TmjMap } from "src/engine/campaign/tmj";

/** What a picked file is loaded as: the map, and the file's name for the error that names it. */
export type PickedLevel = Extract<LevelSource, { map: TmjMap }>;

/**
 * Read a file's text as a Tiled map.
 *
 * Only the shape a map cannot lack is checked — `layers` and `tilesets`
 * arrays. Everything else is `parseLevel`'s to fault on, and a second copy of
 * its rules here would be the copy that goes stale.
 */
export function readLevelFile(name: string, text: string): PickedLevel {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`${name} is not JSON — export the map from Tiled as .tmj`);
  }
  const map = parsed as Partial<TmjMap> | null;
  if (
    !map ||
    typeof map !== "object" ||
    !Array.isArray(map.layers) ||
    !Array.isArray(map.tilesets)
  ) {
    throw new Error(`${name} is not a Tiled map — it has no layers or tilesets`);
  }
  return { name, map: map as TmjMap };
}
